import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { SUSPENDED_HOLDING_URL } from 'app/core/auth/auth.interceptor';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { personaFor } from 'app/core/auth/persona-mapping';
import { PostLoginRedirectService } from 'app/core/app-shell/post-login-redirect.service';
import { WorkspaceService } from 'app/shared/services/workspace/workspace.service';

/**
 * PendingSetupGuard — gates /setup (SPEC-web-planner-013 AC-13). Only the Admin
 * of the selected client, while that client is PendingSetup, may open the
 * organisation setup form. Anyone else is sent through redirectAfterLogin.
 * One deliberate difference from routeFor: when the status cannot be read the
 * Admin still gets the form here (redirectAfterLogin would send them home).
 * Bouncing would start another read and could loop; the server answers 409
 * client_not_pending if the client is not pending, which goes home.
 */
@Injectable({ providedIn: 'root' })
export class PendingSetupGuard implements CanActivate {
    constructor(
        private _authService: AuthService,
        private _workspaceService: WorkspaceService,
        private _postLoginRedirect: PostLoginRedirectService,
        private _router: Router,
    ) {}

    canActivate(): Observable<boolean | UrlTree> {
        const workspaceId = this._authService.selectedTenant?.id;
        const user = this._authService.user;
        if (!workspaceId || personaFor(user as any, workspaceId) !== 'admin') {
            return of(this.away());
        }
        // AC-26s: getClientStatus also reads a 403 client_suspended as Suspended,
        // which goes straight to the holding page (redirectAfterLogin would read
        // SeatLimit again only to land there).
        return this._workspaceService.getClientStatus(workspaceId).pipe(
            map((status) => !status || status === 'PendingSetup' ? true
                : status === 'Suspended' ? this._router.parseUrl(SUSPENDED_HOLDING_URL)
                : this.away()),
        );
    }

    private away(): false {
        this._postLoginRedirect.redirectAfterLogin(this._authService.user);
        return false;
    }
}
